import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useMemo } from "react";
import { reactNl2Br } from "@devowl-wp/react-utils";
import { CookieProperty } from "./cookieProperty.js";
import { CookiePropertyDataTransmission } from "./cookiePropertyDataTransmission.js";
import { CookiePropertyListLessRelevant } from "./cookiePropertyListLessRelevant.js";
import { CookiePropertyTechnicalDefinitionsTable } from "./cookiePropertyTechnicalDefinitionsTable.js";
import { useAppropriateSafeguards } from "../../../hooks/common/useAppropriateSafeguards.js";
import { useBannerOrBlocker } from "../../../hooks/useBannerOrBlocker.js";
import { useLegalLinks } from "../../../hooks/useLegalLinks.js";
import { GcmConsentType } from "../gcm/consentType.js";
const CookiePropertyList = ({ cookie: { name, purpose, isProviderCurrentWebsite, provider, providerContact = {}, providerPrivacyPolicyUrl, providerLegalNoticeUrl, legalBasis, dataProcessingInCountries, dataProcessingInCountriesSpecialTreatments, isEmbeddingOnlyExternalResources, technicalDefinitions, codeDynamics, googleConsentModeConsentTypes }, isEssentialGroup, isDisabled })=>{
    const { i18n, iso3166OneAlpha2, websiteOperator, isGcm, designVersion } = useBannerOrBlocker();
    const { deprecated: i18nDeprecated, legalBasis: i18nLegalBasis } = i18n;
    const { dataProcessingInUnsafeCountries, appropriateSafeguards } = useAppropriateSafeguards({
        dataProcessingInCountries,
        specialTreatments: dataProcessingInCountriesSpecialTreatments
    });
    // Calculate provider-specific details
    const { legalNotice, privacyPolicy, contactForm } = useLegalLinks();
    const providerData = useMemo(()=>{
        if (isProviderCurrentWebsite && websiteOperator) {
            const { address, country, contactEmail, contactPhone } = websiteOperator;
            return {
                provider: [
                    address,
                    iso3166OneAlpha2[country] || country
                ].filter(Boolean).join(", "),
                contact: {
                    email: contactEmail,
                    phone: contactPhone,
                    link: contactForm
                },
                legalNoticeUrl: legalNotice === false ? "" : legalNotice.url,
                privacyPolicyUrl: privacyPolicy === false ? "" : privacyPolicy.url
            };
        }
        return {
            provider,
            contact: providerContact,
            privacyPolicyUrl: providerPrivacyPolicyUrl,
            legalNoticeUrl: providerLegalNoticeUrl
        };
    }, [
        isProviderCurrentWebsite,
        provider,
        providerContact,
        providerPrivacyPolicyUrl,
        providerLegalNoticeUrl,
        websiteOperator,
        legalNotice,
        privacyPolicy,
        contactForm
    ]);
    const hasProviderContact = useMemo(()=>Object.values(providerData.contact).filter(Boolean).length > 0, [
        providerData.contact
    ]);
    const legalBasisValue = useMemo(()=>{
        const isLegalBasisLegalRequirement = legalBasis === "legal-requirement";
        const isLegalBasisLegitimateInterest = legalBasis === "legitimate-interest" || isEssentialGroup;
        if (designVersion <= 11) {
            return isLegalBasisLegalRequirement ? i18nDeprecated.legalRequirement : isLegalBasisLegitimateInterest ? i18n.legitimateInterest : i18n.consent;
        } else {
            const { consentPersonalData, consentStorage, legitimateInterestPersonalData, legitimateInterestStorage, legalRequirementPersonalData } = i18nLegalBasis;
            return [
                // Personal data
                isLegalBasisLegalRequirement ? legalRequirementPersonalData : isLegalBasisLegitimateInterest ? legitimateInterestPersonalData : consentPersonalData,
                // Storage
                !isEmbeddingOnlyExternalResources && (isLegalBasisLegalRequirement ? legitimateInterestStorage : isLegalBasisLegitimateInterest ? legitimateInterestStorage : consentStorage)
            ].filter(Boolean).join(", ");
        }
    }, [
        designVersion,
        legalBasis,
        isEssentialGroup,
        i18nLegalBasis,
        isEmbeddingOnlyExternalResources
    ]);
    return /*#__PURE__*/ _jsxs(Fragment, {
        children: [
            !!purpose && /*#__PURE__*/ _jsx(CookieProperty, {
                label: i18n.purpose,
                value: reactNl2Br(purpose)
            }),
            /*#__PURE__*/ _jsx(CookieProperty, {
                label: i18n.legalBasis.label,
                value: legalBasisValue
            }),
            isGcm && googleConsentModeConsentTypes.length > 0 && /*#__PURE__*/ _jsx(CookieProperty, {
                label: i18n.gcm.dataProcessingInService,
                printValueAs: "empty",
                children: /*#__PURE__*/ _jsx("div", {
                    style: {
                        display: "inline-block"
                    },
                    children: /*#__PURE__*/ _jsx(CookieProperty, {
                        printValueAs: "empty",
                        children: googleConsentModeConsentTypes.map((type)=>/*#__PURE__*/ _jsx(GcmConsentType, {
                                type: type,
                                isDisabled: isDisabled
                            }, type))
                    })
                })
            }),
            /*#__PURE__*/ _jsx(CookieProperty, {
                label: i18n.provider,
                value: providerData.provider,
                children: hasProviderContact && /*#__PURE__*/ _jsxs(Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(CookieProperty, {
                            label: i18n.providerContactPhone,
                            value: providerData.contact.phone,
                            printValueAs: "phone"
                        }),
                        /*#__PURE__*/ _jsx(CookieProperty, {
                            label: i18n.providerContactEmail,
                            value: providerData.contact.email,
                            printValueAs: "mail"
                        }),
                        /*#__PURE__*/ _jsx(CookieProperty, {
                            label: i18n.providerContactLink,
                            value: providerData.contact.link
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(CookieProperty, {
                label: i18n.providerPrivacyPolicyUrl,
                value: providerData.privacyPolicyUrl
            }),
            /*#__PURE__*/ _jsx(CookieProperty, {
                label: i18n.providerLegalNoticeUrl,
                value: providerData.legalNoticeUrl
            }),
            designVersion < 10 && dataProcessingInUnsafeCountries.length > 0 && /*#__PURE__*/ _jsx(CookieProperty, {
                label: i18nDeprecated.dataProcessingInUnsafeCountries,
                value: dataProcessingInUnsafeCountries.join(", ")
            }),
            designVersion < 10 && appropriateSafeguards.length > 0 && /*#__PURE__*/ _jsx(CookieProperty, {
                label: i18nDeprecated.appropriateSafeguard,
                value: appropriateSafeguards.join(", ")
            }),
            /*#__PURE__*/ _jsxs(CookiePropertyListLessRelevant, {
                expandable: designVersion > 9,
                labelModifications: {
                    [i18n.technicalCookieName]: i18n.technicalCookieDefinitions
                },
                groupLabel: name,
                children: [
                    designVersion > 9 && /*#__PURE__*/ _jsx(CookiePropertyDataTransmission, {
                        dataProcessingInCountries: dataProcessingInCountries,
                        dataProcessingInCountriesSpecialTreatments: dataProcessingInCountriesSpecialTreatments
                    }),
                    !isEmbeddingOnlyExternalResources && /*#__PURE__*/ _jsx(CookiePropertyTechnicalDefinitionsTable, {
                        codeDynamics: codeDynamics,
                        definitions: technicalDefinitions
                    })
                ]
            })
        ]
    });
};
export { CookiePropertyList };
